import { useCallback, useEffect, useState } from 'react';
import type { TotpEnrollBegin, TotpStatus } from '@salonomy/types';
import { Button, Card, Field, Pill, TextInput } from '@salonomy/ui';
import { api, ApiClientError, unwrap } from '../../lib/api.js';
import { useToast } from '../../lib/toast.js';

/**
 * OPTIONAL TOTP 2FA for the signed-in account (the WS6 deferred TODO). Self-gating on the HUMAN, not
 * the tenant role — every account can opt in, so this mounts in both the manager and non-manager
 * Settings branches. The secret is shown as TEXT plus an otpauth:// URI (no QR dependency, per brief).
 * Recovery codes are shown ONCE at enable and never again.
 */
export function SecurityPanel() {
  const toast = useToast();
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [begin, setBegin] = useState<TotpEnrollBegin | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disableValue, setDisableValue] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(unwrap(await api.GET('/v1/auth/2fa/status')));
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function startEnroll() {
    setBusy(true);
    try {
      setBegin(unwrap(await api.POST('/v1/auth/2fa/enroll/begin')));
      setCode('');
      setRecoveryCodes(null);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not begin enrollment.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnroll() {
    setBusy(true);
    try {
      const res = unwrap(
        await api.POST('/v1/auth/2fa/enroll/verify', { body: { code: code.trim() } }),
      );
      setRecoveryCodes(res.recoveryCodes);
      setBegin(null);
      setCode('');
      await loadStatus();
      toast.success('Two-factor authentication enabled');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'That code was not valid.');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      const value = disableValue.trim();
      // Six digits → treat as a TOTP code; anything else → the account password.
      const body = /^\d{6}$/.test(value) ? { code: value } : { password: value };
      unwrap(await api.POST('/v1/auth/2fa/disable', { body }));
      setDisableValue('');
      setRecoveryCodes(null);
      await loadStatus();
      toast.success('Two-factor authentication disabled');
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.message : 'Could not disable. Check the code/password.',
      );
    } finally {
      setBusy(false);
    }
  }

  const enrolled = status?.enrolled ?? false;

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div className="section-title">Two-factor authentication</div>
        {enrolled ? <Pill tone="cobalt">On</Pill> : <Pill tone="neutral">Off</Pill>}
      </div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        Add a one-time code from an authenticator app to your sign-in. Optional — your login is
        unchanged until you turn it on.
      </div>

      {/* Recovery codes — shown ONCE, immediately after enabling. */}
      {recoveryCodes ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600 }}>Save your recovery codes</div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
            Each works once if you lose your authenticator. We only store a hash — you won’t see
            these again.
          </div>
          <div
            className="mono"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 6,
              padding: 12,
              borderRadius: 8,
              background: 'var(--slm-surface-2, #f4f5f7)',
            }}
          >
            {recoveryCodes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void navigator.clipboard
                  .writeText(recoveryCodes.join('\n'))
                  .then(() => toast.success('Recovery codes copied'))
                  .catch(() => toast.error('Could not copy — select them manually.'));
              }}
            >
              Copy codes
            </Button>
            <Button size="sm" onClick={() => setRecoveryCodes(null)}>
              I’ve saved them
            </Button>
          </div>
        </div>
      ) : null}

      {/* Enrollment in progress: show the secret + otpauth URI as text, then confirm a code. */}
      {!enrolled && begin ? (
        <div style={{ marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
            Add this account to your authenticator app — scan the link, or type the secret in
            manually.
          </div>
          <Field label="Setup key (manual entry)">
            <TextInput readOnly value={begin.secret} className="mono" />
          </Field>
          <Field label="otpauth link">
            <TextInput readOnly value={begin.otpauthUri} className="mono" />
          </Field>
          <Field label="Enter the 6-digit code to confirm" htmlFor="enroll-code">
            <TextInput
              id="enroll-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
            />
          </Field>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button onClick={confirmEnroll} disabled={busy || code.trim().length !== 6}>
              Enable
            </Button>
            <Button variant="ghost" onClick={() => setBegin(null)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {/* Idle states: offer enable (off) or disable (on). */}
      {!begin && !recoveryCodes ? (
        enrolled ? (
          <div style={{ marginTop: 16 }}>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
              {status?.recoveryCodesRemaining ?? 0} recovery code
              {(status?.recoveryCodesRemaining ?? 0) === 1 ? '' : 's'} remaining. To turn off,
              confirm a current code or your password.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Field label="Current code or password" htmlFor="disable-2fa">
                <TextInput
                  id="disable-2fa"
                  type="text"
                  value={disableValue}
                  onChange={(e) => setDisableValue(e.target.value)}
                  placeholder="123456 or your password"
                />
              </Field>
              <Button variant="ghost" onClick={disable} disabled={busy || !disableValue.trim()}>
                Turn off
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            <Button onClick={startEnroll} disabled={busy}>
              Set up two-factor authentication
            </Button>
          </div>
        )
      ) : null}
    </Card>
  );
}
